import { UnifiedKeyframe, EntityState } from './store';

export function lerp(start: number, end: number, progress: number) {
    return start + (end - start) * progress;
}

export function getInterpolatedEntities(keyframes: UnifiedKeyframe[], currentTime: number): Record<string, EntityState> {
    const interpolated: Record<string, EntityState> = {};
    if (keyframes.length === 0) return interpolated;

    // Collect all entity IDs
    const allEntityIds = new Set<string>();
    keyframes.forEach(kf => Object.keys(kf.entities).forEach(id => allEntityIds.add(id)));

    allEntityIds.forEach(id => {
        try {
            // Find keyframes that specifically define this entity
            const kfsWithEntity = keyframes.filter(kf => kf.entities[id] !== undefined);

            if (kfsWithEntity.length === 0) return;
            if (kfsWithEntity.length === 1) {
                interpolated[id] = { ...kfsWithEntity[0].entities[id] };
                return;
            }

            let startKf = kfsWithEntity[0];
            let endKf = kfsWithEntity[kfsWithEntity.length - 1];

            if (currentTime <= startKf.time) {
                interpolated[id] = { ...startKf.entities[id] };
                return;
            }
            if (currentTime >= endKf.time) {
                interpolated[id] = { ...endKf.entities[id] };
                return;
            }

            for (let i = 0; i < kfsWithEntity.length - 1; i++) {
                if (currentTime >= kfsWithEntity[i].time && currentTime <= kfsWithEntity[i + 1].time) {
                    startKf = kfsWithEntity[i];
                    endKf = kfsWithEntity[i + 1];
                    break;
                }
            }

            if (startKf.time === endKf.time || currentTime === startKf.time) {
                interpolated[id] = { ...startKf.entities[id] };
                return;
            }

            const progress = (currentTime - startKf.time) / (endKf.time - startKf.time);

            const startNode = startKf.entities[id];
            const endNode = endKf.entities[id];

            let finalX = lerp(startNode.x, endNode.x, progress);
            let finalY = lerp(startNode.y, endNode.y, progress);
            let finalAttachedTo = startNode.attachedTo || null;

            // Strict interpolation fallback handling if attachedTo matching frames occurs
            if (id === 'ball') {
                 if (startNode.attachedTo && startNode.attachedTo === endNode.attachedTo) {
                      const pStart = kfsWithEntity.find(k => k.time === startKf.time)?.entities[startNode.attachedTo];
                      const pEnd = kfsWithEntity.find(k => k.time === endKf.time)?.entities[endNode.attachedTo];

                      if (pStart && pEnd) {
                          const diffXStart = startNode.x - pStart.x;
                          const diffYStart = startNode.y - pStart.y;
                          const diffXEnd = endNode.x - pEnd.x;
                          const diffYEnd = endNode.y - pEnd.y;

                          const pCurX = lerp(pStart.x, pEnd.x, progress);
                          const pCurY = lerp(pStart.y, pEnd.y, progress);

                          finalX = pCurX + lerp(diffXStart, diffXEnd, progress);
                          finalY = pCurY + lerp(diffYStart, diffYEnd, progress);
                      }
                 } else {
                     finalAttachedTo = null; // Air flying / Transition phase
                 }
            }

            interpolated[id] = {
                ...startNode,
                x: finalX,
                y: finalY,
                attachedTo: finalAttachedTo
            };

        } catch (e) {
            console.warn(`Error interpolating entity ${id}:`, e);
        }
    });

    return interpolated;
}
